/**
 * Single source of truth for common UI action/state Iconify icon strings.
 *
 * Rule: any component using a generic UI icon (close, download, info, etc.)
 * MUST import from here. Never hardcode a UI icon string inline.
 *
 * To change an icon app-wide: edit one value here.
 *
 * For channel/marketing icons see: @/lib/channel-icons
 */
export const UI_ICONS = {
  // --- Actions ---
  close: 'mdi:close',
  add: 'mdi:plus',
  minus: 'mdi:minus',
  edit: 'solar:pen-bold',
  delete: 'solar:trash-bin-trash-bold',
  deleteMinimalistic: 'solar:trash-bin-minimalistic-outline',
  save: 'mdi:content-save-outline',
  saveFilled: 'mdi:content-save',
  saveOutline: 'solar:diskette-outline',
  copy: 'mdi:content-copy',
  download: 'material-symbols:download-rounded',
  downloadOutline: 'solar:download-outline',
  galleryAddOutline: 'solar:gallery-add-outline',
  upload: 'mdi:upload',
  uploadOutline: 'solar:upload-outline',
  search: 'mdi:magnify',
  searchFill: 'eva:search-fill',
  filter: 'mdi:filter-outline',
  refresh: 'mdi:refresh',
  send: 'mdi:send-outline',
  share: 'mdi:share-variant-outline',
  shareVariant: 'mdi:share-variant',
  print: 'mdi:printer-outline',
  export: 'mdi:export-variant',
  exportPlain: 'mdi:export',
  import: 'mdi:import',
  history: 'mdi:history',
  palette: 'mdi:palette',
  colorFill: 'mdi:format-color-fill',

  // --- Visibility ---
  show: 'mdi:eye-outline',
  hide: 'mdi:eye-off-outline',
  fullscreen: 'mdi:fullscreen',
  fullscreenExit: 'mdi:fullscreen-exit',
  expand: 'mdi:chevron-down',
  altArrowDownOutline: 'solar:alt-arrow-down-outline',
  collapse: 'mdi:chevron-up',

  // --- Navigation ---
  back: 'mdi:arrow-left',
  arrowLeftOutline: 'solar:arrow-left-outline',
  forward: 'mdi:arrow-right',
  arrowRightOutline: 'solar:arrow-right-outline',
  up: 'mdi:arrow-up',
  down: 'mdi:arrow-down',
  externalLink: 'mdi:open-in-new',
  menu: 'mdi:menu',
  more: 'mdi:dots-vertical',
  moreHoriz: 'mdi:dots-horizontal',

  // --- Status / feedback ---
  info: 'mdi:information-outline',
  help: 'mdi:help-circle-outline',
  helpCircle: 'mdi:help-circle',
  warning: 'mdi:alert-outline',
  error: 'mdi:alert-circle-outline',
  success: 'mdi:check-circle-outline',
  checkCircleOutline: 'solar:check-circle-outline',
  closeCircleOutline: 'solar:close-circle-outline',
  pending: 'mdi:clock-outline',
  loading: 'mdi:timer-sand',
  check: 'mdi:check',

  // --- Media controls ---
  play: 'mdi:play',
  pause: 'mdi:pause',
  pauseOutline: 'solar:pause-outline',
  playOutline: 'solar:play-outline',
  playCircle: 'mdi:play-circle',
  playCircleOutline: 'mdi:play-circle-outline',
  stop: 'mdi:stop',

  // --- Editor / Canvas toolbar ---
  cropSquare: 'mdi:crop-square',
  filePdfBox: 'mdi:file-pdf-box',
  aspectRatio: 'mdi:aspect-ratio',
  circleShape: 'mdi:circle-outline',
  triangleShape: 'mdi:triangle-outline',
  highlight: 'mdi:marker',
  lineTool: 'mdi:vector-line',
  layers: 'mdi:layers-outline',
  lock: 'mdi:lock-outline',
  lockOpen: 'mdi:lock-open-variant-outline',
  brush: 'mdi:brush',
  dashboard: 'mdi:view-dashboard-outline',
  compassOutline: 'mdi:compass-outline',
  zoomIn: 'mdi:magnify-plus-outline',
  zoomOut: 'mdi:magnify-minus-outline',
  blurOff: 'mdi:blur-off',
  codeTags: 'mdi:code-tags',

  // --- Data / content ---
  chart: 'mdi:chart-bar',
  chartLine: 'mdi:chart-line',
  chartTimeline: 'mdi:chart-timeline-variant',
  fileDocument: 'mdi:file-document-outline',
  trendUp: 'mdi:trending-up',
  trendDown: 'mdi:trending-down',
  table: 'mdi:table',
  database: 'mdi:database-outline',
  file: 'mdi:file-outline',
  fileTextOutline: 'solar:file-text-outline',
  folder: 'mdi:folder-outline',
  folderOutline: 'solar:folder-outline',
  folderOpenOutline: 'solar:folder-open-outline',
  image: 'mdi:image-outline',
  document: 'solar:document-bold',
  documentOutline: 'solar:document-outline',
  documentTextOutline: 'solar:document-text-outline',

  // --- People / identity ---
  user: 'mdi:account-outline',
  users: 'mdi:account-group-outline',
  usersGroup: 'solar:users-group-rounded-bold',
  usersGroupOutline: 'solar:users-group-rounded-outline',
  userAdd: 'mdi:account-plus-outline',
  userRemove: 'mdi:account-minus-outline',
  contactBook: 'solar:user-id-bold',
  contacts: 'mdi:contacts-outline',

  // --- Misc UI ---
  creditCardOutline: 'mdi:credit-card-outline',
  attachMoney: 'mdi:currency-usd',
  bugReport: 'mdi:bug-outline',
  home: 'mdi:home-outline',
  imageSearch: 'mdi:image-search-outline',
  instagram: 'mdi:instagram',
  campaign: 'mdi:bullhorn-outline',
  slideshow: 'mdi:view-carousel-outline',
  lightbulbOutline: 'mdi:lightbulb-outline',
  addPhotoAlternate: 'mdi:image-plus',
  settings: 'mdi:cog-outline',
  settingsOutline: 'solar:settings-outline',
  settingsBold: 'solar:settings-bold',
  notification: 'mdi:bell-outline',
  calendar: 'mdi:calendar-outline',
  calendarOutline: 'solar:calendar-outline',
  clockOutline: 'mdi:clock-outline',
  location: 'mdi:map-marker-outline',
  globalOutline: 'solar:global-outline',
  phoneOutline: 'solar:phone-outline',
  letterOutline: 'solar:letter-outline',
  linkOutline: 'solar:link-outline',
  chartOutline: 'solar:chart-outline',
  qrCodeOutline: 'solar:qr-code-outline',
  mapOutline: 'solar:map-point-outline',
  laptopMinimalistic: 'solar:laptop-minimalistic-outline',
  locationMap: 'bx:map',
  addLocation: 'ri:map-pin-add-line',
  locationGps: 'mdi:crosshairs-gps',
  tag: 'mdi:tag-outline',
  store: 'mdi:storefront-outline',
  cart: 'mdi:cart-outline',
  monitor: 'mdi:monitor',

  // --- Retail / context icons used in predictions ---
  shoppingCart: 'mdi:cart-outline',
  computer: 'mdi:monitor',
  offer: 'mdi:tag-outline',
  storefront: 'mdi:storefront-outline',

  // --- Workflow / Automation Flow nodes ---
  workflowAudience: 'mdi:account-group',
  workflowAudienceAnonymous: 'mdi:account-clock-outline',
  workflowAudienceSftp: 'mdi:folder-network-outline',
  workflowEnrichAudience: 'mdi:account-details',
  workflowEnrichPropensity: 'mdi:chart-timeline-variant',
  workflowSchedule: 'mdi:clock-outline',
  workflowCondition: 'mdi:filter-variant',
  workflowAction: 'mdi:lightning-bolt',
  workflowEmail: 'mdi:email-send',
  workflowExit: 'mdi:exit-run',
  workflowActionUpdateField: 'mdi:pencil',
  workflowActionAddToList: 'mdi:playlist-plus',
  workflowActionRemoveList: 'mdi:playlist-minus',
  workflowActionNotification: 'mdi:bell-ring',
  workflowActionExport: 'mdi:download',
  workflowEnrichDemographic: 'mdi:account-details',
  workflowEnrichBehavioral: 'mdi:chart-line',
  workflowEnrichGeographic: 'mdi:map-marker',
  workflowEnrichPsychographic: 'mdi:brain',
  workflowEnrichCustom: 'mdi:puzzle',
  workflowEnrichDefault: 'mdi:database-plus',
  workflowSectionDataSource: 'mdi:database-outline',
  workflowSectionProcessing: 'mdi:cog-outline',
  workflowSectionOutput: 'mdi:export-variant',
  workflowPanelCollapse: 'eva:arrow-ios-forward-fill',
  workflowPanelExpand: 'eva:arrow-ios-back-fill',

  // --- AI / ML ---
  ai: 'humbleicons:ai',
  sparkles: 'mdi:sparkles',
  autoFixHigh: 'mdi:auto-fix',

  // --- Prediction model types ---
  predModelLookalike: 'material-symbols:groups',
  predModelLeadScoring: 'material-symbols:ads-click',
  predModelPersona: 'material-symbols:person-check',
  predModelCustom: 'material-symbols:tune',

  // --- Status indicators ---
  checkCircle: 'solar:check-circle-bold',
  closeCircle: 'solar:close-circle-bold',

  // --- Brand / theme builder ---
  colorPalette: 'solar:pallete-2-bold',
  textField: 'solar:text-field-bold',
  settingsMinimal: 'solar:settings-minimalistic-bold',
  galleryWide: 'solar:gallery-wide-bold',
  starFall: 'solar:star-fall-bold',
  heart: 'solar:heart-bold',
  addCircle: 'solar:add-circle-bold',
  reset: 'solar:restart-bold',
  sun: 'solar:sun-2-bold',
  moon: 'solar:moon-bold',
  altArrowUp: 'solar:alt-arrow-up-bold',
  altArrowDown: 'solar:alt-arrow-down-bold',
  userId: 'solar:user-id-bold',
  shieldCheck: 'solar:shield-check-bold',
  arrowRight: 'solar:arrow-right-bold',
  checkBold: 'solar:check-bold',
  graphUp: 'solar:graph-up-bold',
  infoCircle: 'solar:info-circle-line',

  // --- Actions ---
  saveDisk: 'solar:diskette-bold',
  tuning: 'solar:tuning-2-bold',

  archive: 'solar:archive-linear',
  inboxIn: 'solar:inbox-in-bold',
  payment: 'solar:card-bold',
  terminate: 'solar:stop-circle-bold',
  activate: 'solar:play-bold',
  duplicate: 'solar:copy-bold',
  view: 'solar:eye-linear',
  sync: 'solar:refresh-circle-bold',
  narrative: 'solar:document-linear',
  enrich: 'solar:database-linear',

  // --- File states ---
  mailInbox: 'solar:inbox-linear',
  mailTag: 'solar:tag-linear',
  mailLike: 'solar:like-linear',
  mailDislike: 'solar:dislike-linear',
  mailMoon: 'solar:moon-linear',
  mailDanger: 'solar:danger-triangle-linear',
  mailMenu: 'solar:menu-dots-linear',
  mailStar: 'solar:star-linear',
  mailInboxIn: 'solar:inbox-in-linear',
  mailUnread: 'solar:letter-unread-linear',
  mailSent: 'solar:plain-2-linear',
  mailRead: 'solar:letter-opened-linear',
  chat: 'solar:chat-round-line-linear',

  // --- File states ---
  fileCheck: 'mdi:file-check',
  cloudUpload: 'mdi:cloud-upload-outline',
  attachment: 'solar:paperclip-linear',

  // --- Chart types ---
  chartPie: 'mdi:chart-pie',

  // --- Wallet / payment UI ---
  addBold: 'mdi:plus',
  listBold: 'solar:list-bold',
  eyeBold: 'solar:eye-bold',
  downloadBold: 'solar:download-bold',
  billListDuotone: 'solar:bill-list-bold-duotone',
  swap: 'eva:swap-fill',
  arrowIosUp: 'eva:arrow-ios-upward-fill',
  arrowIosDown: 'eva:arrow-ios-downward-fill',
  walletBack: 'solar:multiple-forward-left-bold-duotone',
  walletTotal: 'solar:wallet-bold-duotone',
  walletCredit: 'solar:arrow-down-bold',
  walletDebit: 'eva:arrow-ios-upward-fill',
  walletRefund: 'eva:swap-fill',
  walletExport: 'eva:download-fill',
  bankOutline: 'mdi:bank-outline',

  // --- Connectivity ---
  connection: 'mdi:connection',
  powerPlug: 'mdi:power-plug',

  // --- Favorites ---
  star: 'mdi:star-outline',
  starFilled: 'mdi:star',

  // --- Creative / content production ---
  creatives: 'solar:gallery-add-bold',

  // --- Empty state / no-data icons ---
  noResults: 'solar:magnifer-bold',
  emptyAnalytics: 'solar:monitor-linear',
  emptyChart: 'solar:chart-2-bold',
  emptyChartSimple: 'solar:chart-2-bold',
  emptyInbox: 'solar:inbox-line-bold',
  emptyInboxSimple: 'solar:inbox-line-bold',
  emptyLocation: 'solar:map-point-bold',
  emptyLocationSimple: 'solar:map-point-bold',
  emptyAlbum: 'solar:album-bold',
  emptyPredictions: 'solar:graph-up-bold',
  emptyBrands: 'solar:palette-bold',
  emptyAudience: 'solar:users-group-two-rounded-bold',
  emptyMail: 'solar:letter-linear',
  emptyCreatives: 'solar:gallery-bold',
  emptyQrCode: 'solar:qr-code-bold',

  // --- Account navigation ---
  brandManagement: 'solar:tag-bold',
  subscription: 'solar:bill-list-bold',
  teamAccess: 'solar:users-group-rounded-bold',
  integrations: 'solar:plug-circle-bold',
  billing: 'solar:card-bold',

  // --- Lists / targeting ---
  list: 'mdi:format-list-bulleted',
  target: 'mdi:bullseye-arrow',

  // --- KPI card metrics (use these in all KPICard icon props) ---
  // Campaigns
  kpiTotalCampaigns: 'solar:target-bold',
  kpiActiveCampaigns: 'solar:play-circle-bold',
  kpiPausedCampaigns: 'solar:pause-circle-bold',
  kpiDraftCampaigns: 'solar:document-bold',

  // Mail / delivery
  kpiTotalSent: 'solar:plain-2-bold',
  kpiTotalMailed: 'solar:letter-bold',
  kpiDeliveryRate: 'solar:check-circle-bold',
  kpiScanned: 'solar:eye-bold',
  kpiReadRate: 'solar:eye-bold',

  // Email engagement
  kpiOpened: 'solar:eye-linear',
  kpiClicked: 'solar:cursor-linear',
  kpiReplied: 'solar:reply-bold',
  kpiBounced: 'solar:letter-unread-bold',

  // Audiences / CDM
  kpiTotalAudiences: 'solar:users-group-two-rounded-bold',
  kpiActiveAudiences: 'solar:user-bold',
  kpiReach: 'solar:target-outline',
  kpiGrowthRate: 'solar:chart-arrow-up-bold',

  // Predictions / ML
  kpiTotalPredictions: 'solar:chart-2-bold',
  kpiActiveModels: 'solar:database-bold',
  kpiAccuracy: 'solar:target-bold',
  kpiBusinessLift: 'solar:graph-up-bold',

  // QR Code
  kpiTotalScans: 'solar:qr-code-bold',
  kpiUniqueScans: 'solar:user-check-bold',
  kpiConversions: 'solar:chart-square-bold',

  // Journeys / Workflows
  kpiEngagements: 'solar:hand-shake-bold',
  kpiRevenue: 'solar:dollar-minimalistic-bold',
  kpiAvgDuration: 'solar:clock-circle-bold',
  kpiNodes: 'solar:layers-bold',
  kpiEnrolled: 'solar:user-plus-bold',

  // Logic files
  kpiTotalFiles: 'solar:document-bold',
  kpiActiveFiles: 'solar:play-circle-bold',
  kpiExecutions: 'solar:bolt-bold',
  kpiSuccessRate: 'solar:check-circle-bold',

  // Creatives
  kpiTotalCreatives: 'solar:gallery-bold',
  kpiOrders: 'solar:cart-large-bold',

  // General KPI
  kpiSpend: 'solar:dollar-minimalistic-bold',
  kpiImpressions: 'solar:eye-bold',
  kpiActions: 'solar:play-bold',
  kpiCampaignGoal: 'solar:flag-bold',

  // --- Automation Flow / NodeSettingsPanel Refactored Icons ---
  infoFilled: 'mdi:information',
  databaseFilled: 'mdi:database',
  fileUpload: 'mdi:file-upload',
  mapMarkerPath: 'mdi:map-marker-path',
  truckMoving: 'mdi:truck-moving',
  checkCircleFilled: 'mdi:check-circle',
  alertCircleFilled: 'mdi:alert-circle',
  chartBellCurve: 'mdi:chart-bell-curve',
  accountBadge: 'mdi:account-badge',
  calendarClock: 'mdi:calendar-clock',
  flash: 'mdi:flash',
  emailOutline: 'mdi:email-outline',
  bellRingOutline: 'mdi:bell-ring-outline',
  emailPlus: 'mdi:email-plus',
  formatText: 'mdi:format-text',
  link: 'mdi:link',
  codeBraces: 'mdi:code-braces',

  // --- Voice / Blyn Agent Shared Icons ---
  pen2: 'solar:pen-2-bold',
  searchLinear: 'solar:magnifer-linear',
  uploadMinimalistic: 'solar:upload-minimalistic-bold',
  folderError: 'solar:folder-error-bold',
  phoneCallingDuotone: 'solar:phone-calling-bold-duotone',
  phoneBold: 'solar:phone-bold',
  transferHorizontal: 'solar:round-transfer-horizontal-bold',
  altArrowRightLinear: 'solar:alt-arrow-right-linear',
  microphoneLinear: 'solar:microphone-linear',

  // --- Workflow Templates / Node categories ---
  workflowRouting2: 'solar:routing-2-outline',
  workflowQualifier: 'solar:routing-bold',
  workflowCallback: 'solar:phone-calling-bold',
  workflowCompliance: 'solar:chat-round-dots-bold',
  workflowSurvey: 'solar:clipboard-list-bold',
  workflowUserSpeak: 'solar:user-speak-bold',
  workflowBolt: 'solar:bolt-bold',
  workflowAlarm: 'solar:alarm-bold',
  workflowSnowflake: 'solar:snowflake-bold',

  // --- Solar Linear set for Blyn Agent ---
  closeLine: 'mingcute:close-line',
  addCircleLinear: 'solar:add-circle-linear',
  arrowDownLinear: 'solar:alt-arrow-down-linear',
  arrowLeftLinear: 'solar:alt-arrow-left-linear',
  arrowRightLinear: 'solar:alt-arrow-right-linear',
  arrowRightLinear2: 'solar:arrow-right-linear',
  bellLinear: 'solar:bell-linear',
  boltLinear: 'solar:bolt-linear',
  bookLinear: 'solar:book-2-linear',
  brainLinear: 'solar:brain-linear',
  chatDotsLinear: 'solar:chat-dots-linear',
  chatLineLinear: 'solar:chat-line-linear',
  checkCircleLinear: 'solar:check-circle-linear',
  checkRead: 'solar:check-read-linear',
  checkSquare: 'solar:check-square-linear',
  clapperboardLinear: 'solar:clapperboard-linear',
  clockCircleLinear: 'solar:clock-circle-linear',
  closeCircleLinear: 'solar:close-circle-linear',
  dangerTriangleLinear: 'solar:danger-triangle-linear',
  documentTextLinear: 'solar:document-text-linear',
  downloadLinear: 'solar:download-linear',
  folderLinear: 'solar:folder-linear',
  folderWithFilesLinear: 'solar:folder-with-files-linear',
  historyLinear: 'solar:history-linear',
  layersLinear: 'solar:layers-linear',
  pauseLinear: 'solar:pause-linear',
  pencil2Linear: 'solar:pen-2-linear',
  pencilLinear: 'solar:pen-linear',
  pinLinear: 'solar:pin-linear',
  questionCircleLinear: 'solar:question-circle-linear',
  restartLinear: 'solar:restart-linear',
  rocketLinear: 'solar:rocket-linear',
  routeLinear: 'solar:route-linear',
  settingsSolarLinear: 'solar:settings-linear',
  shareSolarLinear: 'solar:share-linear',
  uploadSolarLinear: 'solar:upload-linear',
  usersGroupTwoLinear: 'solar:users-group-two-rounded-linear',

  // --- Voice / Builder Page Icons ---
  chatRoundDotsBold: 'solar:chat-round-dots-bold',
  pauseCircleBold: 'solar:pause-circle-bold',
  playCircleBold: 'solar:play-circle-bold',
  robotBold: 'solar:robot-bold',
  chatRoundLineBoldDuotone: 'solar:chat-round-line-bold-duotone',
  globalLinear: 'solar:global-linear',
  lockPasswordBold: 'solar:lock-password-bold',
  cloudUploadBold: 'solar:cloud-upload-bold',
  downloadMinimalisticLinear: 'solar:download-minimalistic-linear',
  linkCircleLinear: 'solar:link-circle-linear',
  testTubeBold: 'solar:test-tube-bold',
  penNewSquareBold: 'solar:pen-new-square-bold',
  aiUser: 'hugeicons:ai-user',
  shieldWarningBold: 'solar:shield-warning-bold',
  clockCircleLineDuotone: 'solar:clock-circle-line-duotone',

  // --- Voice Contacts / Campaigns / Analytics / Workflows Page Icons ---
  uploadBold: 'solar:upload-bold',
  userPlusBold: 'solar:user-plus-bold',
  usersGroupTwoDuotone: 'solar:users-group-two-rounded-bold-duotone',
  usersGroupTwoBold: 'solar:users-group-two-rounded-bold',
  arrowLeftBold: 'solar:arrow-left-bold',
  dangerTriangleBold: 'solar:danger-triangle-bold',
  linkBold: 'solar:link-bold',
  rocketBold: 'solar:rocket-bold',
  fileCheckBold: 'solar:file-check-bold',
  phoneCallingRoundedBold: 'solar:phone-calling-rounded-bold',
  chartSquareBold: 'solar:chart-square-bold',
  fileTextBold: 'solar:file-text-bold',
  chevronLeft: 'mdi:chevron-left',
  chevronRight: 'mdi:chevron-right',
  firstPage: 'mdi:page-first',
  lastPage: 'mdi:page-last',
  undoLeftLinear: 'solar:undo-left-linear',
  undoRightLinear: 'solar:undo-right-linear',
  fitToScreen: 'mdi:fit-to-screen-outline',
  settingsBoldDuotone: 'solar:settings-bold-duotone',
  calendarDateBold: 'solar:calendar-date-bold',
  copyBoldDuotone: 'solar:copy-bold-duotone',
  deleteDuotone: 'solar:trash-bin-trash-bold-duotone',
  cellularNetworkBold: 'solar:cellular-network-bold',
  wifiBold: 'solar:wi-fi-bold',
  batteryChargeBold: 'solar:battery-charge-bold',
  microphoneBold: 'solar:microphone-bold',
  userRobotBold: 'solar:user-robot-bold',
  folderWithFilesBold: 'solar:folder-with-files-bold',
  linkRoundBold: 'solar:link-round-bold',
  checkReadBold: 'solar:check-read-bold',
  arrowRightBold: 'solar:arrow-right-bold',
  copyBold: 'solar:copy-bold',
  phoneCallingBold: 'solar:phone-calling-bold',
  shieldCheckBold: 'solar:shield-check-bold',

  // --- Voice Campaigns / Workflows Page Icons (added for brandbook icon-config compliance) ---
  arrowDownBold: 'solar:arrow-down-bold',
  arrowRightUpBold: 'solar:arrow-right-up-bold',
  buildings2Bold: 'solar:buildings-2-bold',
  calendarAddBold: 'solar:calendar-add-bold',
  calendarMarkBold: 'solar:calendar-mark-bold',
  chartSquareBoldDuotone: 'solar:chart-square-bold-duotone',
  clockCircleBold: 'solar:clock-circle-bold',
  iphoneBold: 'solar:iphone-bold',
  logoutBold: 'solar:logout-bold',
  magniferBold: 'solar:magnifer-bold',
  mailboxBoldDuotone: 'solar:mailbox-bold-duotone',
  mailboxLineDuotone: 'solar:mailbox-line-duotone',
  menuDotsBold: 'solar:menu-dots-bold',
  plain2Bold: 'solar:plain-2-bold',
  qrCodeBold: 'solar:qr-code-bold',
  questionCircleBold: 'solar:question-circle-bold',
  userCircleBold: 'solar:user-circle-bold',
  recordCircleBold: 'solar:record-circle-bold',
  shareCircleBold: 'solar:share-circle-bold',
  shuffleBold: 'solar:shuffle-bold',
  sidebarMinimalisticBold: 'solar:sidebar-minimalistic-bold',
  sidebarMinimalisticLinear: 'solar:sidebar-minimalistic-linear',
  starBold: 'solar:star-bold',
  transferHorizontalBold: 'solar:transfer-horizontal-bold',
  transmissionBoldDuotone: 'solar:transmission-bold-duotone',
  userSpeakBold: 'solar:user-speak-bold',
  userSpeakRoundedBold: 'solar:user-speak-rounded-bold',

  // --- Voice Workflow Node Palette Icons ---
  calculatorMinimalisticBold: 'solar:calculator-minimalistic-bold',
  fireBold: 'solar:fire-bold',
  magniferZoomInBold: 'solar:magnifer-zoom-in-bold',
  repeatBold: 'solar:repeat-bold',
  sirenRoundedBold: 'solar:siren-rounded-bold',
  satelliteBold: 'solar:satellite-bold',
  doorOpenBold: 'solar:door-open-bold',
  forbiddenCircleBold: 'solar:forbidden-circle-bold',

  // --- Release Notes Section Icons ---
  sortByTimeBold: 'solar:sort-by-time-bold',
  bugBold: 'solar:bug-bold',

  // --- Voice Agent Builder Config Icons ---
  home2Bold: 'solar:home-2-bold',
  sunBold: 'solar:sun-bold',
  buildingsBold: 'solar:buildings-bold',
  shieldUserBold: 'solar:shield-user-bold',
  homeSmileBold: 'solar:home-smile-bold',
  tuningBold: 'solar:tuning-bold',
  userBold: 'solar:user-bold',
  bookBold: 'solar:book-bold',
  widgetBold: 'solar:widget-bold',
  hospitalBold: 'solar:hospital-bold',
  wheelBold: 'solar:wheel-bold',
  caseRoundMinimalisticBold: 'solar:case-round-minimalistic-bold',
  emojiFunnyCircleBold: 'solar:emoji-funny-circle-bold',
  notesBold: 'solar:notes-bold',
  clipboardListBold: 'solar:clipboard-list-bold',
  boltBold: 'solar:bolt-bold',
  letterBold: 'solar:letter-bold',
  mapPointBold: 'solar:map-point-bold',
  billListBold: 'solar:bill-list-bold',
} as const;

/**
 * Icons used exclusively in the map topbar actions.
 * Import from here instead of inlining strings in topbarActions.ts.
 */
export const MAP_TOPBAR_ICONS = {
  // Panels
  panelHide: 'mdi:chevron-left',
  panelRight: 'mdi:chevron-right',
  audience: 'mdi:account-group-outline',
  marketArea: 'mdi:map-marker-radius-outline',
  layers: 'mdi:layers-outline',
  layersOff: 'mdi:layers-off',
  controls: 'mdi:cog-outline',
  eddmRoutes: 'mdi:map-marker-path',

  // View modes
  normalView: 'mdi:view-dashboard-outline',
  view3D: 'mdi:cube-outline',

  // Drawing
  drawPolygon: 'mdi:draw',
  drawPolygonActive: 'mdi:draw-pen',
  savePolygon: 'mdi:content-save-outline',

  // Right panel / results
  results: 'hugeicons:chart-up',
  dataSummary: 'hugeicons:chart-up',

  // Overlays
  legend: 'mdi:information-outline',
  pointsOfInterest: 'mdi:store',
  guide: 'mdi:help-circle-outline',
} as const;

export type MapTopbarIconKey = keyof typeof MAP_TOPBAR_ICONS;

export type UiIconKey = keyof typeof UI_ICONS;


/**
 * Icons for dashboard DataGrid row actions (DashboardGridActionKind).
 * Import from here instead of hardcoding icon strings in DashboardGridActions.
 */
export const DASHBOARD_ACTION_ICONS = {
  view: UI_ICONS.view,
  edit: UI_ICONS.edit,
  duplicate: UI_ICONS.duplicate,
  delete: UI_ICONS.delete,
  activate: UI_ICONS.activate,
  terminate: UI_ICONS.terminate,
  add: UI_ICONS.add,
  export: UI_ICONS.download,
  more: UI_ICONS.moreHoriz,
  sync: UI_ICONS.sync,
  archive: UI_ICONS.archive,
  payment: UI_ICONS.payment,
  support: UI_ICONS.help,
  narrative: UI_ICONS.narrative,
  enrich: UI_ICONS.enrich,
} as const;
